import { useEffect, useState } from "react";
import { fetchMidpointMediaOpenProject } from "../../../api/midpointMediaAdminApi";
import MidpointMediaSetupPanel from "../../../components/midpointMedia/admin/MidpointMediaSetupPanel";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../../components/clientPortal/ClientPortalStates";
import useMidpointMediaAdminView from "../../../hooks/useMidpointMediaAdminView";
import "../../../styles/midpointMediaAdmin.css";

export default function MidpointMediaAdminProjectsPage() {
  const { clientViewId } = useMidpointMediaAdminView();
  const [state, setState] = useState({ loading: true, error: "", payload: null });

  useEffect(() => {
    if (!clientViewId) return undefined;
    let cancelled = false;
    fetchMidpointMediaOpenProject(clientViewId)
      .then((payload) => {
        if (!cancelled) setState({ loading: false, error: "", payload });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error: error.message, payload: null });
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId]);

  if (!clientViewId) return <p className="empty-state">No workspace assigned.</p>;
  if (state.loading) return <ClientPortalLoading message="Loading OpenProject…" />;
  if (state.error && !state.payload) return <ClientPortalError message={state.error} />;

  const live = Boolean(state.payload?.live);
  const projects = live ? state.payload.projects : state.payload?.demo?.projects || [];
  const totals = live ? state.payload.totals : state.payload?.demo?.totals;

  return (
    <div className="mm-admin-page">
      <header>
        <p className="mm-admin-kicker">Projects</p>
        <h1>OpenProject health</h1>
        <p className="mm-admin-lead">
          Delivery status from the OpenProject instance hosted on DigitalOcean. Until the API
          token is set, this page shows a representative demo and the exact env vars to add.
        </p>
      </header>

      <section className="mm-admin-grid">
        <article className="mm-admin-card">
          <h3>Projects</h3>
          <div className="mm-admin-kpi">
            {totals?.projects ?? projects.length}
            <span>{live ? "From OpenProject" : "Demo count"}</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <h3>Open work packages</h3>
          <div className="mm-admin-kpi">
            {totals?.openWorkPackages ?? "—"}
            <span>Across visible projects</span>
          </div>
        </article>
      </section>

      <section className="mm-admin-card">
        {live ? (
          <span className="mm-admin-badge mm-admin-badge--live">Live</span>
        ) : (
          <span className="mm-admin-badge mm-admin-badge--demo">Demo data</span>
        )}
        <table className="mm-admin-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Status</th>
              <th>Open</th>
              <th>Closed</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project.id}>
                <td>{project.name}</td>
                <td>{project.status?.replace("_", " ") || "—"}</td>
                <td>{project.open ?? "—"}</td>
                <td>{project.closed ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {!live ? (
        <MidpointMediaSetupPanel
          instructions={state.payload?.instructions}
          error={state.payload?.error}
        />
      ) : state.payload?.baseUrl ? (
        <p>
          <a href={state.payload.baseUrl} target="_blank" rel="noreferrer">
            Open OpenProject
          </a>
        </p>
      ) : null}
    </div>
  );
}
